/**
 * Utility to extract unique theme IDs from events
 * Used to fetch only necessary themes instead of all themes
 */

import type { EventTeaser } from '../api/types';
import type { Event } from '../types';

/**
 * Event-like type that has themes
 */
type EventWithThemes = EventTeaser | Event | { theme?: Array<{ id?: string; name?: string } | null> | null };

/**
 * Extract unique theme IDs from an array of events
 * 
 * @param events - Array of events (EventTeaser or Event)
 * @returns Array of unique theme IDs
 * 
 * @example
 * ```ts
 * const events = await getEvents();
 * const themeIds = extractThemeIdsFromEvents(events);
 * const themes = await themesClient.fetchThemesByIds(themeIds);
 * ```
 */
export function extractThemeIdsFromEvents(events: EventWithThemes[]): string[] {
  const themeIds = new Set<string>();
  
  for (const event of events) {
    if (event.theme && Array.isArray(event.theme)) {
      for (const theme of event.theme) {
        if (theme?.id) {
          themeIds.add(theme.id);
        }
      }
    }
  }
  
  return Array.from(themeIds);
}

/**
 * Extract unique theme names from an array of events
 * 
 * @param events - Array of events (EventTeaser or Event)
 * @returns Array of unique theme names
 */
export function extractThemeNamesFromEvents(events: EventWithThemes[]): string[] {
  const themeNames = new Set<string>();
  
  for (const event of events) {
    if (event.theme && Array.isArray(event.theme)) {
      for (const theme of event.theme) {
        if (theme?.name) {
          themeNames.add(theme.name);
        }
      }
    }
  }
  
  return Array.from(themeNames);
}

/**
 * Extract unique themes (id + name) from an array of events
 * 
 * @param events - Array of events (EventTeaser or Event)
 * @returns Array of unique themes with id and name
 */
export function extractThemesFromEvents(events: EventWithThemes[]): Array<{ id: string; name: string }> {
  const themesMap = new Map<string, { id: string; name: string }>();
  
  for (const event of events) {
    if (event.theme && Array.isArray(event.theme)) {
      for (const theme of event.theme) {
        if (theme?.id && theme?.name && !themesMap.has(theme.id)) {
          themesMap.set(theme.id, { id: theme.id, name: theme.name });
        }
      }
    }
  }
  
  return Array.from(themesMap.values());
}

